import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    Input,
    Typography,
    Card,
    Space,
    Spin,
    App,
    Button
} from 'antd';
import {
    SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {authService, customerService} from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import debounce from 'lodash/debounce';

const { Text } = Typography;

const CustomersList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [Customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(false);

    const observer = useRef();
    const lastCustomerElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMoreCustomers();
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, error]);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Клиенты');

        setRightContent(
            authService.hasPermission('pos:customers:create') && (<Button
                type="primary"
                onClick={() => navigate('/customers/create')}
            >
                Создать клиента
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Загрузка записей при первой загрузке и при изменении поиска
    useEffect(() => {
        setError(false);
        fetchCustomers();
    }, [searchText]);

    // Функция для дебаунсинга поиска
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchText(value);
        }, 500),
        []
    );

    // Обработчик изменения поля поиска
    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Загрузка записей
    const fetchCustomers = async () => {
        try {
            setLoading(true);
            setCustomers([]);
            setNextCursor(null);
            setHasMore(true);

            const response = await customerService.getCustomers({
                search: searchText
            });

            if (response && response.status === 'success') {
                setCustomers(response.data.items);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить список клиентов');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке клиентов');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Загрузка дополнительных клиентов при прокрутке
    const loadMoreCustomers = async () => {
        if (!hasMore || loading || error) return;

        try {
            setLoading(true);

            const response = await customerService.getCustomers({
                cursor: nextCursor,
                search: searchText
            });

            if (response && response.status === 'success') {
                setCustomers(prev => [...prev, ...response.data.items]);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше клиентов');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных клиентов');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Фамилия и имя',
            dataIndex: 'full_name',
            key: 'fullName',
            width: 300,
            render: (text, record) => (
                <Text strong>
                    {record.full_name}
                </Text>
            ),
        },
        {
            title: 'Номер карты',
            dataIndex: 'card_number',
            key: 'cartNumber',
            width: 170,
        },
        {
            title: 'Баланс',
            dataIndex: 'balance',
            key: 'balance',
            width: 100,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        }
    ];

    // Создаем данные для таблицы
    const tableData = Customers.map((Customer, index) => ({
        ...Customer,
        key: Customer.id
    }));

    return (
        <>
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <Input
                            placeholder="Поиск клиентов..."
                            prefix={<SearchOutlined />}
                            onChange={handleSearch}
                            style={{ width: 350 }}
                            allowClear
                        />
                    </Space>
                    <Typography.Text>
                        Всего: {total}
                    </Typography.Text>
                </div>

                {initialLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <style>
                            {`
                                .table-horizontal-borders .ant-table-tbody > tr > td {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-thead > tr > th {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                                    background-color: #f5f5f5 !important;
                                }
                            `}
                        </style>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            size="middle"
                            bordered={false}
                            className="table-horizontal-borders"
                            tableLayout="fixed"
                            scroll={{x: 1000}}
                            onRow={(record) => ({
                                onClick: () => navigate(`/customers/${record.id}`),
                                style: {cursor: 'pointer'}
                            })}
                        />
                        {loading && (
                            <div style={{textAlign: 'center', padding: '20px 0'}}>
                                <Spin/>
                            </div>
                        )}
                        {/* Элемент для отслеживания прокрутки */}
                        {hasMore && (
                            <div
                                ref={lastCustomerElementRef}
                                style={{height: 10, width: '100%'}}
                            />
                        )}
                        {!hasMore && Customers.length > 0 && (
                            <div style={{textAlign: 'center', padding: '10px'}}>
                                <Typography.Text type="secondary">
                                    Больше записей нет
                                </Typography.Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </>
    );
};

export default CustomersList;